import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, skip, switchMap } from 'rxjs/operators';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';

import { InboxActions } from './inbox.actions';
import { InboxSelectors } from './inbox.selectors';

/** No `entityType` anywhere here: `EntityType` has no value for a DM or an alert, so the kind ships
 * as `params.inboxItemType` instead. Widening that union is a #data decision. */
@Injectable()
export class InboxAnalyticsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  /** `eventSource` is the surface the user came from. Anything that names none reports `direct`, so
   * no open is credited to a button nobody pressed. */
  trackInboxOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened),
      map(({ entryPoint }) =>
        SegmentActions.track({ event: SegmentTrackEvent.INBOX_OPENED, params: { eventSource: entryPoint } })
      )
    )
  );

  trackItemClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.itemClicked),
      map(({ itemId, itemType }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.INBOX_ITEM_CLICKED,
          params: { entityId: itemId, inboxItemType: itemType },
        })
      )
    )
  );

  /** Both card buttons, one event; `target` says which. Not folded into `inbox_item_clicked` — that
   * event's `entityId` is the inbox row, this one's is the linked item, and mixing the two id spaces
   * under one name breaks every join on it. */
  trackLinkedItemOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.openLinkedItemInDetail, InboxActions.goToLinkedItem),
      map(({ item, type }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.INBOX_LINKED_ITEM_OPENED,
          params: {
            entityId: item.itemId,
            teamId: item.teamId,
            userId: item.owner.userId,
            inboxItemType: item.itemType,
            target: type === InboxActions.openLinkedItemInDetail.type ? 'detail' : 'page',
            // A literal because milestone is the only parented type, and `NotificationItemType` has
            // values `EntityType` doesn't.
            ...(item.parent ? { parentEntityID: item.parent.itemId, parentEntity: 'rock' as const } : {}),
          },
        })
      )
    )
  );

  /**
   * Fires off `alertUpdated` rather than the `resolveAlert` request, so a failed resolve doesn't
   * report as a completed one — and filtered to resolutions, since the same action carries reads
   * and unresolves.
   */
  trackAlertResolved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.alertUpdated),
      filter(({ alert }) => !!alert.resolvedAt),
      map(({ alert }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.INBOX_ALERT_RESOLVED,
          params: {
            entityId: alert.id,
            inboxItemType: 'alert',
            severity: alert.severity,
            source: alert.source,
            category: alert.category,
          },
        })
      )
    )
  );

  /** Fires on success, not the request — a thread that fails to open didn't start. `hasMessage`
   * rather than a second event, since composing is the same act either way.
   *
   * Creation is idempotent, so composing to the same person twice reports a second start. Effects
   * see post-reducer state and can't tell the two apart. */
  trackThreadStarted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.createConversationSuccess),
      map(({ conversation, body }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.INBOX_THREAD_STARTED,
          params: {
            entityId: conversation.id,
            inboxItemType: 'message',
            // Same predicate `sendComposedMessage$` uses, so a whitespace-only body reads as empty
            // in the data and in the behaviour.
            hasMessage: !!body?.trim(),
          },
        })
      )
    )
  );

  /** Filtering is routing, so the first emission after an open is the page load, not a filter
   * change — hence the `skip(1)`. Restarting on every `opened` keeps leaving and returning quiet. */
  trackFilterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened),
      switchMap(() => this.actions$.pipe(ofType(InboxActions.filterChanged), skip(1))),
      map(({ filter: view }) =>
        SegmentActions.track({ event: SegmentTrackEvent.INBOX_FILTER_CHANGED, params: { filter: view } })
      )
    )
  );

  trackDirectMessageSent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.sendMessageSuccess),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectConversations)),
      map(([{ message }, conversations]) =>
        SegmentActions.track({
          event: SegmentTrackEvent.INBOX_DIRECT_MESSAGE_SENT,
          params: {
            entityId: message.id,
            inboxItemType: 'message',
            parentEntityID: message.conversationId,
            participantCount: conversations.find(c => c.id === message.conversationId)?.participantIds.length ?? 2,
          },
        })
      )
    )
  );
}
